import { useState, useEffect, useRef, useCallback } from 'react';

import * as SERVICE from '../../../../constants/service.types';
import { ROOT_GROUP_ID } from '../../../../constants/group';
import {
  parseResponseBody,
  prepareBody,
  requestSocketPromiseWithTimeout,
} from '../../../../socket/query';

/**
 *
 * @returns {Promise<{
 *  parentDeviceGroupId: number
 * deviceGroups: CameraDeviceGroup[] }>}
 */
export const getCameraGroupsByParentId = async ({
  namespace,
  parentDeviceGroupId,
}) => {
  const response = await requestSocketPromiseWithTimeout({
    appServiceType: SERVICE.LIST_DEVICE_GROUP_BY_PARENT_DEVICE_GROUP,
    body: prepareBody({
      namespace,
      parentDeviceGroupId,
    }),
  });

  return parseResponseBody(response);
};

/**
 * 
 * @param {*} param0 
 * @returns {Promise<{
 *  deviceGroup: CameraDeviceGroup & {
 *   devices: Camera[]
 *  }
 *  deviceGroupId: number
 *  total: number
 *  page: number
 *  size: number
  timeRequest: number }>}
 */
export const getDeviceByGroupId = async ({
  // page,
  // size,
  namespace,
  deviceGroupId,
  timeRequest,
}) => {
  const response = await requestSocketPromiseWithTimeout({
    appServiceType: SERVICE.LIST_DEVICE_BY_DEVICE_GROUP_ID,
    body: prepareBody({
      namespace,
      deviceGroupId,
      timeRequest,
    }),
  });

  return parseResponseBody(response);
};

/**
 * Data structure:
 *  Group map: GroupId -> Group // lookup by id
 *  Tree map: GroupId -> [groupId,...] // relationship in flat object
 *  Camera map: GroupId -> [camera, camera,...] // cameras of each group
 *  Get children:
 *   (1)If: treemap has children:
 *     GroupId -> children -> GroupMap[childrenId][]
 *   Else:
 *     fetch children -> assign to group map, assign to treemap -> go to 1
 */ export const useCameraGroups = ({ namespace }) => {
  const groupMap = useRef({});
  const treeMap = useRef({});
  const cameraMap = useRef({});
  const [data, setData] = useState();

  useEffect(() => {
    getCameraGroupsByParentId({
      namespace,
      parentDeviceGroupId: ROOT_GROUP_ID,
    }).then(setData);
  }, [namespace]);

  useEffect(() => {
    if (data?.deviceGroups) {
      data.deviceGroups.forEach((group) => {
        groupMap.current[group.id] = group;
      });
    }
  }, [data]);

  const getChildren = useCallback(
    (groupId) => {
      if (treeMap.current[groupId]) {
        return Promise.resolve(
          treeMap.current[groupId].map((id) => groupMap.current[id])
        );
      }

      return getCameraGroupsByParentId({
        namespace,
        parentDeviceGroupId: groupId,
      }).then((subgroupData) => {
        treeMap.current[groupId] = subgroupData.deviceGroups.map(
          (child) => child.id
        );

        subgroupData.deviceGroups.forEach((child) => {
          groupMap.current[child.id] = child;
        });

        return treeMap.current[groupId].map((id) => groupMap.current[id]);
      });
    },
    [namespace]
  );

  const getCameras = useCallback(
    (groupId) => {
      if (cameraMap.current[groupId]) {
        return Promise.resolve(cameraMap.current[groupId]);
      }

      return getDeviceByGroupId({
        namespace,
        deviceGroupId: groupId,
      }).then(({ cameraInfos }) => {
        cameraMap.current[groupId] = cameraInfos;

        return cameraInfos;
      });
    },
    [namespace]
  );

  const rootGroups = data?.deviceGroups?.filter(
    (group) => group.parentDeviceGroupId === ROOT_GROUP_ID
  );

  const getPath = (groupId) => {
    if (!groupId) {
      return [groupId];
    }

    const path = [];

    while (groupId !== ROOT_GROUP_ID) {
      path.push(groupId);
      if (groupMap.current[groupId]) {
        groupId = groupMap.current[groupId].parentDeviceGroupId;
      } else {
        break;
      }
    }

    path.push(ROOT_GROUP_ID);

    return path.reverse();
  };

  return {
    rootGroups,
    getChildren,
    getCameras,
    groupMap,
    treeMap,
    cameraMap,
    getPath,
  };
};
